import { useState } from 'react'

import { formatClock } from '@krp/client-runtime'
import { Button, SelectField, TextField } from '@krp/ui-kit'

import { findDriver, ATOL_DRIVER_ID } from '../model/drivers'
import { ATOL_MODELS, kassaCard, kassaEnabled } from '../model/kassas'
import type { EquipmentSnapshot, HardwareActions, HardwareLinks, HardwareTabId } from '../model/types'
import { Mark } from './Mark'
import { blockReason, type TabProps } from './tabSupport'
import { ScanPanel } from './ScanPanel'

function ManualKassaForm({ snapshot, actions, locked, busy }: TabProps & { snapshot: EquipmentSnapshot; actions: HardwareActions }) {
  const settings = snapshot.equipment?.settings
  // Черновик формы — память окна: пока человек не нажал «Включить», ничего никуда не уходит.
  const [model, setModel] = useState(String(settings?.model ?? 500))
  const [port, setPort] = useState<'0' | '1'>(settings?.port === 0 ? '0' : '1')
  const [comFile, setComFile] = useState(settings?.comFile ?? '')
  const enabled = kassaEnabled(snapshot)
  const comMissing = port === '0' && comFile.trim().length === 0
  const reason = blockReason(locked, busy) ?? (comMissing ? 'Укажите имя COM-порта' : null)

  return (
    <form
      className="hw-form"
      aria-label="Настройка кассы"
      onSubmit={(event) => {
        event.preventDefault()
      }}
    >
      <SelectField
        label="Модель кассы"
        actionId="platform.hardware.atol-model"
        value={model}
        options={ATOL_MODELS.map((item) => ({ value: String(item.value), label: item.label }))}
        onChange={setModel}
      />
      <SelectField
        label="Подключение"
        actionId="platform.hardware.atol-port"
        value={port}
        options={[
          { value: '1', label: 'USB' },
          { value: '0', label: 'COM-порт' },
        ]}
        onChange={(value) => setPort(value === '0' ? '0' : '1')}
      />
      {port === '0' && <TextField label="Имя COM-порта" actionId="platform.hardware.atol-com" value={comFile} placeholder="COM3" onChange={setComFile} />}
      <div className="hw-buttons">
        <Button
          variant="primary"
          actionId="platform.hardware.enable-atol"
          disabled={reason !== null}
          disabledReason={reason}
          onClick={() =>
            actions.connect({
              category: 'fiscal-register',
              kind: ATOL_DRIVER_ID,
              candidateId: 'manual',
              values: { model, port, comFile: comFile.trim() },
            })
          }
        >
          {enabled ? 'Применить настройку' : 'Включить кассу'}
        </Button>
      </div>
    </form>
  )
}

/**
 * Вкладка «Кассы» (п. 2.2, 3.3, 3.4): сканирование, карточка включённой кассы, «Подключить вручную».
 * Одна касса на компьютер. Без связи с агентом форм настройки нет (п. 3.1).
 */
export function KassasTab({
  snapshot,
  links,
  actions,
  locked,
  busy,
  available,
  onOpenTab,
}: TabProps & {
  snapshot: EquipmentSnapshot
  links: HardwareLinks
  actions: HardwareActions
  /** Есть ли с кем говорить (агент найден или были его данные). */
  available: boolean
  onOpenTab: (tab: HardwareTabId) => void
}) {
  const [manual, setManual] = useState(false)
  const card = kassaCard(snapshot)
  const driver = findDriver(snapshot, ATOL_DRIVER_ID)

  if (!available) {
    return (
      <section aria-label="Кассы" className="hw-tab">
        <p className="hw-hint">Недоступно, пока нет связи с программой-драйвером: касса подключается через неё.</p>
      </section>
    )
  }

  return (
    <section aria-label="Кассы" className="hw-tab">
      {driver !== null && driver.state !== 'ready' && (
        <p className="hw-hint">Сначала установите драйвер ККТ 10 — вкладка «Драйверы».</p>
      )}
      {card !== null && (
        <article className="hw-kassa hw-box hw-box--ok" aria-label="Касса">
          <h3 className="hw-title">
            {card.title} · {card.connection}
            {card.serial !== null && <> · № {card.serial}</>}
          </h3>
          <p className="hw-marks">
            <Mark glyph="✓" word="Включена" tone="ok" />{' '}
            <Mark glyph={card.linkOk === true ? '✓' : card.linkOk === false ? '✕' : '…'} word={card.linkWord} tone={card.linkOk === true ? 'ok' : card.linkOk === false ? 'block' : 'wait'} />{' '}
            <Mark glyph={card.canSell ? '✓' : '✕'} word={card.saleWord} tone={card.canSell ? 'ok' : 'block'} />
          </p>
          {card.shiftLine !== null && <p>{card.shiftLine}</p>}
          <div aria-label="Неполадки кассы">
            {card.problems.length === 0 ? (
              <p className="hw-hint">Неполадок нет.</p>
            ) : (
              <ul className="hw-list">
                {card.problems.map((problem) => (
                  <li key={problem.key} className={`hw-problem hw-problem--${problem.severity}`}>
                    <span aria-hidden="true">{problem.glyph}</span> <strong>{problem.label}</strong> {problem.text}
                    {problem.action !== null && <> {problem.action}</>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {card.test !== null && (
            <p role="status" className="hw-hint">
              {card.test.ok ? '✓' : '✕'} {card.test.message} (проверено {formatClock(card.test.checkedAt)})
            </p>
          )}
          <div className="hw-buttons">
            <Button
              variant="secondary"
              actionId="platform.hardware.kassa-test"
              disabled={locked || busy}
              disabledReason={blockReason(locked, busy)}
              onClick={actions.testKassa}
            >
              Проверить связь
            </Button>
            <Button
              variant="secondary"
              actionId="platform.hardware.disable-atol"
              disabled={locked || busy}
              disabledReason={blockReason(locked, busy)}
              onClick={actions.disableKassa}
            >
              Выключить кассу
            </Button>
          </div>
        </article>
      )}

      <ScanPanel snapshot={snapshot} category="fiscal-register" links={links} actions={actions} locked={locked} busy={busy} onOpenTab={onOpenTab} />

      {!locked && driver?.state === 'ready' && (
        <div>
          <Button variant="ghost" actionId="platform.hardware.toggle" aria-expanded={manual} onClick={() => setManual((value) => !value)}>
            Подключить вручную
          </Button>
          {manual && <ManualKassaForm snapshot={snapshot} actions={actions} locked={locked} busy={busy} />}
        </div>
      )}
    </section>
  )
}
